/**
 * @module utils/crosstab
 * @description 透视表 crosstab 结构归一化与 QB 默认构建（上传契约：ICrosstabFieldRef[]）
 */

/** 与 UserFilter/FilterPanel/constant 一致，避免重依赖链 */
const DATALAB_COMPARE_USER_FILTERS = 'DATALAB_COMPARE_USER_FILTERS';

export interface ICrosstabFieldRef {
  table_code: string;
  column: string;
}

export interface ICrosstabConfig {
  rows: ICrosstabFieldRef[];
  columns: ICrosstabFieldRef[];
}

export function crosstabFieldKey(ref: ICrosstabFieldRef): string {
  const tableCode = ref.table_code ?? '';
  return `${tableCode}_${ref.column}`;
}

export function toCrosstabFieldRefFromQB(raw: unknown): ICrosstabFieldRef | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const column = String(row.column_name ?? row.column ?? '').trim();
  if (!column) {
    return null;
  }
  const isCompare =
    column === DATALAB_COMPARE_USER_FILTERS ||
    String(row.column_type ?? '') === 'compare_user_filters';
  return {
    table_code: isCompare ? '' : String(row.table_code ?? 't0').trim() || 't0',
    column,
  };
}

function buildQbLookup(qbList: unknown[]): Map<string, ICrosstabFieldRef> {
  const map = new Map<string, ICrosstabFieldRef>();
  for (const raw of qbList) {
    const ref = toCrosstabFieldRefFromQB(raw);
    if (!ref) {
      continue;
    }
    map.set(ref.column, ref);
    map.set(crosstabFieldKey(ref), ref);
  }
  return map;
}

/**
 * 将 rows/columns 一侧归一为 ICrosstabFieldRef[]（兼容历史 string[] 脏数据）
 */
export function normalizeCrosstabSide(
  items: unknown[] | undefined,
  qbList: unknown[],
): ICrosstabFieldRef[] {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }
  const lookup = buildQbLookup(qbList);
  const result: ICrosstabFieldRef[] = [];
  const seen = new Set<string>();

  for (const item of items) {
    let ref: ICrosstabFieldRef | null = null;

    if (typeof item === 'string') {
      const name = item.trim();
      if (!name) {
        continue;
      }
      ref =
        lookup.get(name) ??
        (name === DATALAB_COMPARE_USER_FILTERS
          ? { table_code: '', column: name }
          : { table_code: 't0', column: name });
    } else if (item && typeof item === 'object') {
      const row = item as Record<string, unknown>;
      const column = String(row.column ?? row.column_name ?? '').trim();
      if (!column) {
        continue;
      }
      const isCompare = column === DATALAB_COMPARE_USER_FILTERS;
      ref = {
        table_code: isCompare
          ? ''
          : String(row.table_code ?? 't0').trim() || 't0',
        column,
      };
    }

    if (!ref) {
      continue;
    }
    const key = crosstabFieldKey(ref);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(ref);
  }

  return result;
}

/**
 * 清除 rows∩columns 重叠：同一字段不可同时在两侧。
 * 列区优先保留（透视维度已在 columns 时不应再出现在 rows）。
 */
export function dedupeCrosstabRowsAgainstColumns(
  config: ICrosstabConfig,
): ICrosstabConfig {
  if (!config.columns.length || !config.rows.length) {
    return config;
  }
  const columnKeys = new Set(config.columns.map(crosstabFieldKey));
  const nextRows = config.rows.filter(
    ref => !columnKeys.has(crosstabFieldKey(ref)),
  );
  if (nextRows.length === config.rows.length) {
    return config;
  }
  return { rows: nextRows, columns: config.columns };
}

export function normalizeCrosstabConfig(
  raw: unknown,
  columnDimensions: unknown[],
  columnMetrics: unknown[],
): ICrosstabConfig | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const cfg = raw as { rows?: unknown; columns?: unknown };
  const qbAll = [...columnDimensions, ...columnMetrics];
  return dedupeCrosstabRowsAgainstColumns({
    rows: normalizeCrosstabSide(
      Array.isArray(cfg.rows) ? cfg.rows : [],
      columnDimensions,
    ),
    columns: normalizeCrosstabSide(
      Array.isArray(cfg.columns) ? cfg.columns : [],
      qbAll,
    ),
  });
}

/** 无保存 crosstab 时：rows=全部维度，columns=全部指标（与 PivotTable defaultCrosstab 一致） */
export function buildDefaultCrosstabFromQB(
  columnDimensions: unknown[],
  columnMetrics: unknown[],
): ICrosstabConfig {
  const rows = columnDimensions
    .map(toCrosstabFieldRefFromQB)
    .filter((r): r is ICrosstabFieldRef => r !== null);
  const columns = columnMetrics
    .map(toCrosstabFieldRefFromQB)
    .filter((r): r is ICrosstabFieldRef => r !== null);
  return { rows, columns };
}

function pruneRefs(
  refs: ICrosstabFieldRef[],
  removedKeys: Set<string>,
): ICrosstabFieldRef[] {
  return refs.filter((ref) => !removedKeys.has(crosstabFieldKey(ref)));
}

function appendRefs(
  refs: ICrosstabFieldRef[],
  toAdd: ICrosstabFieldRef[],
): ICrosstabFieldRef[] {
  const seen = new Set(refs.map(crosstabFieldKey));
  const next = [...refs];
  for (const ref of toAdd) {
    const key = crosstabFieldKey(ref);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    next.push(ref);
  }
  return next;
}

function migrateRefs(
  refs: ICrosstabFieldRef[],
  renames: Array<{
    prevColumnName: string;
    nextColumnName: string;
    field: { table_code?: string; column_name: string };
    isDimension: boolean;
  }>,
): ICrosstabFieldRef[] {
  if (!renames.length) {
    return refs;
  }
  return refs.map(ref => {
    const hit = renames.find(
      r =>
        ref.column === r.prevColumnName &&
        (ref.table_code || 't0') === (r.field.table_code || 't0'),
    );
    if (!hit) {
      return ref;
    }
    return {
      ...ref,
      column: hit.nextColumnName,
      table_code: hit.field.table_code || ref.table_code || 't0',
    };
  });
}

export interface ISyncCrosstabFromQBInput {
  existing: ICrosstabConfig | undefined;
  columnDimensions: unknown[];
  columnMetrics: unknown[];
  addedDimensions: Array<{ table_code?: string; column_name: string }>;
  addedMetrics: Array<{ table_code?: string; column_name: string }>;
  removedDimensions: Array<{ table_code?: string; column_name: string }>;
  removedMetrics: Array<{ table_code?: string; column_name: string }>;
  identityRenames: Array<{
    prevColumnName: string;
    nextColumnName: string;
    field: { table_code?: string; column_name: string; builder_type?: string };
    displayOnly: boolean;
  }>;
}

/**
 * QB 变更时增量维护 crosstab（不整表替换为 string[]）
 */
export function syncCrosstabFromQBChange(
  input: ISyncCrosstabFromQBInput,
): ICrosstabConfig | undefined {
  const {
    existing,
    columnDimensions,
    columnMetrics,
    addedDimensions,
    addedMetrics,
    removedDimensions,
    removedMetrics,
    identityRenames,
  } = input;

  const qbAll = [...columnDimensions, ...columnMetrics];
  let config: ICrosstabConfig = existing
    ? {
        rows: normalizeCrosstabSide(existing.rows, columnDimensions),
        columns: normalizeCrosstabSide(existing.columns, qbAll),
      }
    : buildDefaultCrosstabFromQB(columnDimensions, columnMetrics);

  const before = JSON.stringify(config);

  const removedDimKeys = new Set(
    removedDimensions.map(d =>
      crosstabFieldKey({
        table_code: d.table_code || 't0',
        column: d.column_name,
      }),
    ),
  );
  const removedMetKeys = new Set(
    removedMetrics.map(m =>
      crosstabFieldKey({
        table_code: m.table_code || 't0',
        column: m.column_name,
      }),
    ),
  );

  if (removedDimKeys.size) {
    config = {
      ...config,
      rows: pruneRefs(config.rows, removedDimKeys),
      columns: pruneRefs(config.columns, removedDimKeys),
    };
  }
  if (removedMetKeys.size) {
    config = {
      ...config,
      columns: pruneRefs(config.columns, removedMetKeys),
    };
  }

  const dimAdds = addedDimensions
    .map(d =>
      toCrosstabFieldRefFromQB({
        ...d,
        column_name: d.column_name,
        table_code: d.table_code,
        builder_type: 'dimension',
      }),
    )
    .filter((r): r is ICrosstabFieldRef => r !== null);

  const metAdds = addedMetrics
    .map(m =>
      toCrosstabFieldRefFromQB({
        ...m,
        column_name: m.column_name,
        table_code: m.table_code,
        builder_type: 'metric',
      }),
    )
    .filter((r): r is ICrosstabFieldRef => r !== null);

  if (dimAdds.length) {
    // 已在列区的维度是交叉透视结果，进页 empty→full 误报 dimensionAdded 时不可再塞回 rows
    const columnKeys = new Set(config.columns.map(crosstabFieldKey));
    const rowOnlyAdds = dimAdds.filter(
      ref => !columnKeys.has(crosstabFieldKey(ref)),
    );
    if (rowOnlyAdds.length) {
      config = { ...config, rows: appendRefs(config.rows, rowOnlyAdds) };
    }
  }
  if (metAdds.length) {
    config = { ...config, columns: appendRefs(config.columns, metAdds) };
  }

  const idRenames = identityRenames
    .filter(r => !r.displayOnly)
    .map(r => ({
      prevColumnName: r.prevColumnName,
      nextColumnName: r.nextColumnName,
      field: r.field,
      isDimension: r.field.builder_type === 'dimension',
    }));

  if (idRenames.length) {
    config = {
      rows: migrateRefs(config.rows, idRenames),
      columns: migrateRefs(config.columns, idRenames),
    };
  }

  // Confirm/联动结果统一保证 rows∩columns 互斥
  config = dedupeCrosstabRowsAgainstColumns(config);

  if (JSON.stringify(config) === before) {
    return undefined;
  }

  return config;
}

/**
 * 解析当前生效的 crosstab（无持久化时用 QB 默认：行=全部维度，列=全部指标）
 */
export function resolveEffectiveCrosstab(
  crosstab: ICrosstabConfig | undefined,
  columnDimensions: unknown[],
  columnMetrics: unknown[] = [],
): ICrosstabConfig {
  if (
    crosstab &&
    Array.isArray(crosstab.rows) &&
    Array.isArray(crosstab.columns)
  ) {
    return crosstab;
  }
  return buildDefaultCrosstabFromQB(columnDimensions, columnMetrics);
}

/**
 * 是否为交叉透视表：行区维度数（不含对照组）小于 QB 维度总数。
 * 与 Analysis model 请求参数侧 isCrosstab 判定一致。
 */
export function isCrosstabActive(
  crosstab: ICrosstabConfig | undefined,
  columnDimensions: unknown[],
  columnMetrics: unknown[] = [],
): boolean {
  if (!columnDimensions.length) {
    return false;
  }
  const effective = resolveEffectiveCrosstab(
    crosstab,
    columnDimensions,
    columnMetrics,
  );
  const rowsWithoutCompare = effective.rows.filter(
    row => row.column !== DATALAB_COMPARE_USER_FILTERS,
  );
  return rowsWithoutCompare.length < columnDimensions.length;
}
